import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ticketService } from '../../services/api';
import { toast } from 'sonner';
import { 
  Search, 
  CheckCircle, 
  XCircle, 
  Loader2,
  FileText,
  Download,
  Edit,
  Check
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';

// Sources prédéfinies courantes
const PREDEFINED_SOURCES = ['VG', 'MAXES', 'TMB01', 'REACH01', 'TICKETMASTER', 'VIAGOGO', 'FTN', 'STUBHUB', 'ASSAFIS'];

export const RefCheckModal = ({ open, onOpenChange, currentProject, onRefresh }) => {
  const [inputRefs, setInputRefs] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'found', 'missing'
  
  // Selection et modification en masse
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [bulkSource, setBulkSource] = useState('');
  const [updatingSource, setUpdatingSource] = useState(false);

  // Extraire toutes les sources uniques des résultats
  const availableSources = useMemo(() => {
    const sources = new Set(PREDEFINED_SOURCES);
    if (results?.results) {
      results.results.forEach(r => {
        if (r.source && r.source.trim()) {
          sources.add(r.source.trim().toUpperCase());
        }
      });
    }
    return Array.from(sources).sort();
  }, [results]);

  const handleCheck = async () => {
    if (!inputRefs.trim()) {
      toast.error('Veuillez entrer au moins une référence');
      return;
    }

    // Parse references (one per line or comma-separated)
    const refs = inputRefs
      .split(/[\n,;]+/)
      .map(r => r.trim())
      .filter(r => r.length > 0);

    if (refs.length === 0) {
      toast.error('Aucune référence valide trouvée');
      return;
    }

    setLoading(true);
    setFilter('all'); // Reset filter on new check
    try {
      const data = await ticketService.checkReferences(refs, currentProject);
      setResults(data);
      
      if (data.missing > 0) {
        toast.warning(`${data.missing} référence(s) manquante(s) sur ${data.total_checked}`);
      } else {
        toast.success(`Toutes les ${data.found} références sont présentes !`);
      }
    } catch (error) {
      toast.error('Erreur lors de la vérification');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered results based on current filter
  const filteredResults = useMemo(() => {
    if (!results) return [];
    if (filter === 'all') return results.results;
    if (filter === 'found') return results.results.filter(r => r.status === 'TROUVÉ');
    if (filter === 'missing') return results.results.filter(r => r.status === 'MANQUANT');
    return results.results;
  }, [results, filter]);

  const exportResults = () => {
    if (!filteredResults || filteredResults.length === 0) return;
    
    const lines = [
      'RÉFÉRENCE;STATUT;SOURCE;ÉVÉNEMENT;CATÉGORIE;QTY;PRIX;CLIENT;EMAIL;ATTRIBUÉ;DATE VENTE'
    ];
    
    filteredResults.forEach(r => {
      lines.push([
        r.ref,
        r.status,
        r.source || '',
        r.evenement || '',
        r.categorie || '',
        r.qty || '',
        r.prix || '',
        r.nom_client || '',
        r.mail_client || '',
        r.attribue ? 'OUI' : 'NON',
        r.date_vente || ''
      ].join(';'));
    });
    
    const filterSuffix = filter === 'missing' ? '_MANQUANTES' : filter === 'found' ? '_TROUVEES' : '';
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verification_refs${filterSuffix}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(`${filteredResults.length} référence(s) exportée(s)`);
  };

  // Sélectionner/désélectionner tout
  const toggleSelectAll = () => {
    if (selectedRefs.size === filteredResults.filter(r => r.status === 'TROUVÉ').length) {
      setSelectedRefs(new Set());
    } else {
      const foundRefs = filteredResults.filter(r => r.status === 'TROUVÉ').map(r => r.ref);
      setSelectedRefs(new Set(foundRefs));
    }
  };

  // Toggle une sélection individuelle
  const toggleSelect = (ref) => {
    const newSelected = new Set(selectedRefs);
    if (newSelected.has(ref)) {
      newSelected.delete(ref);
    } else {
      newSelected.add(ref);
    }
    setSelectedRefs(newSelected);
  };

  // Modifier la source en masse
  const handleBulkSourceUpdate = async () => {
    if (!bulkSource || selectedRefs.size === 0) return;
    
    setUpdatingSource(true);
    try {
      // Appeler l'API pour mettre à jour la source
      const response = await fetch(`/api/tickets/bulk-update-source`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refs: Array.from(selectedRefs),
          source: bulkSource,
          project: currentProject
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.updated} ticket(s) mis à jour avec la source "${bulkSource}"`);
        
        // Mettre à jour les résultats localement
        setResults(prev => ({
          ...prev,
          results: prev.results.map(r => 
            selectedRefs.has(r.ref) ? { ...r, source: bulkSource } : r
          )
        }));
        
        setSelectedRefs(new Set());
        setBulkSource('');
        
        // Rafraîchir les données
        if (onRefresh) onRefresh();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingSource(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset after animation
    setTimeout(() => {
      setInputRefs('');
      setResults(null);
      setFilter('all');
      setSelectedRefs(new Set());
      setBulkSource('');
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2 text-white">
            <Search className="h-5 w-5 text-[#C4A052]" />
            Vérification de Références VENTE
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Input Section */}
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">
              Collez vos références (une par ligne ou séparées par des virgules)
            </label>
            <Textarea
              value={inputRefs}
              onChange={(e) => setInputRefs(e.target.value)}
              placeholder="635633998&#10;635609730&#10;635612150&#10;..."
              className="min-h-[120px] bg-zinc-800 border-zinc-700 text-white font-mono"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">
                {inputRefs.split(/[\n,;]+/).filter(r => r.trim()).length} référence(s) détectée(s)
              </span>
              <Button
                onClick={handleCheck}
                disabled={loading || !inputRefs.trim()}
                className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Vérifier
              </Button>
            </div>
          </div>

          {/* Results Section */}
          {results && (
            <div className="space-y-3">
              {/* Summary - Clickable filters */}
              <div className="flex gap-4 p-3 bg-zinc-800/50 rounded-lg">
                <div 
                  className={cn(
                    "text-center cursor-pointer rounded-lg p-2 transition-all",
                    filter === 'all' ? "bg-white/10 ring-2 ring-white/30" : "hover:bg-white/5"
                  )}
                  onClick={() => setFilter('all')}
                >
                  <p className="text-2xl font-bold text-white">{results.total_checked}</p>
                  <p className="text-xs text-zinc-400">Vérifiées</p>
                </div>
                <div 
                  className={cn(
                    "text-center cursor-pointer rounded-lg p-2 transition-all",
                    filter === 'found' ? "bg-emerald-500/20 ring-2 ring-emerald-500/50" : "hover:bg-emerald-500/10"
                  )}
                  onClick={() => setFilter('found')}
                >
                  <p className="text-2xl font-bold text-emerald-500">{results.found}</p>
                  <p className="text-xs text-zinc-400">Trouvées</p>
                </div>
                <div 
                  className={cn(
                    "text-center cursor-pointer rounded-lg p-2 transition-all",
                    filter === 'missing' ? "bg-red-500/20 ring-2 ring-red-500/50" : "hover:bg-red-500/10"
                  )}
                  onClick={() => setFilter('missing')}
                >
                  <p className="text-2xl font-bold text-red-500">{results.missing}</p>
                  <p className="text-xs text-zinc-400">Manquantes</p>
                </div>
                <div className="flex-1 flex justify-end items-center gap-2">
                  {filter !== 'all' && (
                    <span className="text-xs text-zinc-400 bg-zinc-700 px-2 py-1 rounded">
                      Filtre: {filter === 'found' ? 'Trouvées' : 'Manquantes'} ({filteredResults.length})
                    </span>
                  )}
                  <Button
                    onClick={exportResults}
                    variant="outline"
                    size="sm"
                    className="border-zinc-600"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exporter {filter !== 'all' ? `(${filteredResults.length})` : 'CSV'}
                  </Button>
                </div>
              </div>

              {/* Bulk Source Update */}
              {selectedRefs.size > 0 && (
                <div className="flex items-center gap-3 p-3 bg-[#C4A052]/10 border border-[#C4A052]/30 rounded-lg">
                  <span className="text-sm text-[#C4A052] font-medium">
                    {selectedRefs.size} sélectionné(s)
                  </span>
                  <Select value={bulkSource} onValueChange={setBulkSource}>
                    <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-600">
                      <SelectValue placeholder="Nouvelle source..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSources.map(src => (
                        <SelectItem key={src} value={src}>{src}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleBulkSourceUpdate}
                    disabled={!bulkSource || updatingSource}
                    size="sm"
                    className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                  >
                    {updatingSource ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Appliquer
                  </Button>
                </div>
              )}

              {/* Results Table */}
              <div className="max-h-[40vh] overflow-auto rounded-lg border border-zinc-700">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-800 sticky top-0">
                    <tr>
                      <th className="p-2 w-8">
                        <Checkbox
                          checked={selectedRefs.size > 0 && selectedRefs.size === filteredResults.filter(r => r.status === 'TROUVÉ').length}
                          onCheckedChange={toggleSelectAll}
                          className="border-zinc-500"
                        />
                      </th>
                      <th className="text-left p-2 text-zinc-400">Statut</th>
                      <th className="text-left p-2 text-zinc-400">Référence</th>
                      <th className="text-left p-2 text-zinc-400">Source</th>
                      <th className="text-left p-2 text-zinc-400">Événement</th>
                      <th className="text-left p-2 text-zinc-400">Cat.</th>
                      <th className="text-left p-2 text-zinc-400">Qty</th>
                      <th className="text-left p-2 text-zinc-400">Prix</th>
                      <th className="text-left p-2 text-zinc-400">Client</th>
                      <th className="text-left p-2 text-zinc-400">Attribué</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((r, idx) => (
                      <tr 
                        key={idx} 
                        className={cn(
                          "border-t border-zinc-700/50",
                          r.status === 'MANQUANT' ? 'bg-red-500/10' : 'bg-emerald-500/5',
                          selectedRefs.has(r.ref) && 'bg-[#C4A052]/20'
                        )}
                      >
                        <td className="p-2">
                          {r.status === 'TROUVÉ' && (
                            <Checkbox
                              checked={selectedRefs.has(r.ref)}
                              onCheckedChange={() => toggleSelect(r.ref)}
                              className="border-zinc-500"
                            />
                          )}
                        </td>
                        <td className="p-2">
                          {r.status === 'TROUVÉ' ? (
                            <span className="flex items-center gap-1 text-emerald-500">
                              <CheckCircle className="h-4 w-4" />
                              OK
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-500">
                              <XCircle className="h-4 w-4" />
                              MANQUANT
                            </span>
                          )}
                        </td>
                        <td className="p-2 font-mono text-white">{r.ref}</td>
                        <td className="p-2">
                          {r.source ? (
                            <span className="px-2 py-0.5 bg-zinc-700 text-[#C4A052] rounded text-xs font-medium">
                              {r.source}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="p-2 text-zinc-300 truncate max-w-[150px]" title={r.evenement}>
                          {r.evenement || '-'}
                        </td>
                        <td className="p-2 text-zinc-300">{r.categorie || '-'}</td>
                        <td className="p-2 text-zinc-300">{r.qty || '-'}</td>
                        <td className="p-2 text-zinc-300">{r.prix ? `${r.prix}€` : '-'}</td>
                        <td className="p-2 text-zinc-300 truncate max-w-[100px]" title={r.nom_client}>
                          {r.nom_client || '-'}
                        </td>
                        <td className="p-2">
                          {r.status === 'TROUVÉ' && (
                            r.attribue ? (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">OUI</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-zinc-500/20 text-zinc-400 rounded text-xs">NON</span>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Missing refs highlight - only show when not filtering */}
              {filter === 'all' && results.missing > 0 && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-medium mb-2">
                    ⚠️ Références manquantes ({results.missing}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {results.results
                      .filter(r => r.status === 'MANQUANT')
                      .map((r, idx) => (
                        <span key={idx} className="px-2 py-1 bg-red-500/20 text-red-300 rounded font-mono text-sm">
                          {r.ref}
                        </span>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RefCheckModal;
